// Lone Rider — Make Configurator
//
// Drives the 5-step configurator used by products tagged `make_configurator`
// (currently /products/mtl9).
//
// These products carry the whole configuration in a SINGLE Shopify option, e.g.
//
//   "BMW R1300GS Adventure | MTL9 Motorcycle Lights set + MotoCAN V2 + Under Nose Mount"
//   "BMW R1300GS Adventure | MTL9 Motorcycle Lights set & MTL4 Motorcycle Lights set + MotoCAN V2 + Bar Clamp Mount"
//
// Shopify caps a product at 3 options, so the five dropdowns cannot be real
// options. Instead they are pure UI: each one is parsed out of the option1
// string, and picking a full set looks the matching variant back up and writes
// its EXACT original option1 value into the hidden native <select>. Dispatching
// `change` from there hands control straight back to theme.Variants, which keeps
// owning price, gallery, ?variant= and the add-to-cart button.
//
// Looking the variant up (rather than re-joining the five parts back into a
// string) is deliberate: it means a stray separator or double space in the
// Shopify admin can never produce an unresolvable value.
//
// The same five steps also fill the sticky cart bar's panel, as a second instance of
// everything below: one option string is no more usable as a dropdown down there than
// it is up here. The two instances are kept on the same answers by syncPeers(), and
// the bar's one drives the bar directly rather than through theme.Variants — see
// createConfigurator() and updateSticky().
//
// Untagged products never reach this file — lr-make-filter.js keeps handling them.
(() => {
  'use strict';

  // Same list as lr-make-filter.js. Kept in sync by hand; both files intentionally
  // stay standalone so a failure in one cannot take out the other.
  const MAKES = ['BMW', 'Ducati', 'Harley Davidson', 'Honda', 'Husqvarna', 'KTM', 'Kawasaki', 'Suzuki', 'Triumph', 'Yamaha'];

  // Longest first, so a multi-word make is never shadowed by a single-word one
  // that happens to be a prefix of it.
  const MAKES_BY_LENGTH = MAKES.slice().sort((a, b) => b.length - a.length);

  const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const makeToRegex = (make) =>
    new RegExp('\\b' + make.split(/\s+/).map(escapeRegExp).join('[\\s-]+') + '\\b', 'i');
  const MAKE_RX = new Map(MAKES.map((m) => [m, makeToRegex(m)]));

  const MOTOCAN_RX = /motocan/i;
  const MOUNT_RX = /\bmounts?\b/i;
  const LIGHTS_RX = /\bmtl\s*-?\s*\d+\b|\blights?\b/i;
  const NO_WORD_RX = /\bno\b/i;

  // Variants with no bike in the title — the lights/mount kit on its own.
  const OTHER_BRAND = 'Other Brand';

  const hasPartKeyword = (s) => MOTOCAN_RX.test(s) || MOUNT_RX.test(s) || LIGHTS_RX.test(s);

  // ---------- Canonical keys ----------
  //
  // The same physical choice gets typed differently across variants — "MTL9 ... set
  // & MTL4 ... set" vs "MTL9 ... Set + MTL4 ... Set", "Under Nose Mount" vs
  // "Under-Nose Mount". Matching on the raw text would show the customer two rows
  // that read identically, each carrying only the mounts that happened to be paired
  // with that spelling — which is exactly the "pick the second MTL9+MTL4 to get Bar
  // Clamp" behaviour we must not have.
  //
  // So every step is *matched* on a canonical key and *displayed* using the first
  // spelling the catalogue used. Lights and Mount then behave as the independent
  // choices they are: pick MTL9 or MTL9+MTL4, then pick Under Nose or Bar Clamp.
  // Accents are stripped rather than dropped: without the NFD pass "Ténéré 700"
  // loses its accented letters entirely and keys as "tnr700", which would neither
  // match a "Tenere 700" spelling of the same bike nor the ETA rule below.
  const normKey = (s) => String(s || '').toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '');

  // Tolerates "MTL9", "MTL 9", "mtl-9".
  const LIGHT_TOKEN_RX = /\bmtl\s*-?\s*(\d+)\b/gi;

  function lightsKey(text) {
    const tokens = [];
    let m;
    LIGHT_TOKEN_RX.lastIndex = 0;
    while ((m = LIGHT_TOKEN_RX.exec(text)) !== null) {
      const token = 'mtl' + m[1];
      if (tokens.indexOf(token) === -1) tokens.push(token);
    }
    // Sorted, so "MTL4 & MTL9" and "MTL9 + MTL4" are one choice rather than two.
    return tokens.length ? tokens.sort().join('+') : normKey(text);
  }

  function mountKey(text) {
    // Crash bar first: "BMW OEM Crash Bar Aux Light Mount" contains "Bar", so a
    // looser bar-clamp test would swallow it.
    if (/crash\s*-?\s*bar/i.test(text)) return 'crashbar';
    if (/bar\s*-?\s*clamp/i.test(text)) return 'barclamp';
    if (/under\s*-?\s*nose/i.test(text)) return 'undernose';
    return normKey(text);
  }

  // Bar Clamp ships with the lights, so it is the "off" side of the Mount ticks.
  // Everything else in the Mount field — Under Nose, BMW OEM Crash Bar Aux Light —
  // is an upgrade the customer ticks on. Keying off the INCLUDED mount rather than
  // naming each upgrade means a new mount name in the catalogue works without a
  // code change.
  const MOUNT_INCLUDED_KEY = 'barclamp';

  // Every upgrade on offer for the current bike: each in-stock mount that is not the
  // included Bar Clamp. A bike may list more than one (the F850 / F900 GS Adventure
  // offer both Under Nose and the BMW OEM Crash Bar mount), so this is a list —
  // renderCheckbox() gives each one its own tick.
  const upgradeMounts = (allowed) => allowed.filter((c) => c.key !== MOUNT_INCLUDED_KEY);

  // The one ticked by default: the first upgrade in the catalogue's own order.
  const upgradeMount = (allowed) => upgradeMounts(allowed)[0] || null;

  // ---------- Model spelling fixes ----------
  //
  // Model rows are matched on the canonical model key, so a bike the catalogue
  // spells two ways splits into two rows. That costs more than a duplicate in the
  // dropdown: the Mount step can only offer an upgrade when the included Bar Clamp
  // and that upgrade sit on the SAME row, so a split bike is left with one mount on
  // each half and loses its mount tick entirely.
  //
  // Each entry maps a mistyped model key onto the spelling that bike should use. The
  // value is the DISPLAY string and the key is re-derived from it, so an aliased
  // variant ends up indistinguishable from one the admin typed correctly — same row,
  // same label, same ETA matching.
  //
  // Correcting the variant title in Shopify is still the better fix; an entry here
  // keeps carrying the typo. Add one when the title cannot be changed.
  const MODEL_ALIASES = {
    // MTL9's "BMW F850 GS (2018–2023) Adventure" (Bar Clamp) puts Adventure after
    // the year AND dates the bike a year earlier than its "BMW F850 GS Adventure
    // (2019–2023)" Crash Bar variant, so the two never met and neither half could
    // show a mount tick. Folded onto the Crash Bar variant's spelling.
    f850gs20182023adventure: 'F850 GS Adventure (2019–2023)'
  };

  // ---------- Mid-November batch ----------
  //
  // The bikes whose kit ships mid-November. A FIXED LIST, deliberately: inventory
  // for these is untracked in Shopify, so `variant.available` is true for them and
  // stock can never be used to tell them apart. This list is the only source of
  // truth — adding or removing a bike is a one-line edit here.
  //
  // lr-make-filter.js keeps its own copy of these bikes, in WAITLIST_MODELS, for the
  // waitlist on products tagged `waitlist` (motocan). The two are deliberately
  // separate — see the comment there — but they say the same thing today, so a bike
  // added or dropped here wants looking at there.
  //
  // Matched as a substring of the canonical model key, which is lowercased with
  // accents and punctuation stripped: "Ténéré 700 (2019–2024)" keys as
  // "tenere70020192024", and "1390 Super Adventure" as "1390superadventure" (so an
  // R / S suffix in the admin still matches).
  const MID_NOVEMBER_MODELS = [
    '1390superadventure',
    // Both Tenere 700 generations. Kept as two separate keys rather than one
    // 'tenere700' prefix so a future generation is opted in deliberately.
    'tenere70020192024',
    'tenere7002025'
  ];

  const isMidNovember = (parsed) =>
    MID_NOVEMBER_MODELS.some((key) => parsed.keys.model.indexOf(key) > -1);

  // ---------- Delivery ETA notices ----------
  //
  // Which notice is shown for the resolved variant. The COPY lives in
  // make-configurator.liquid (Weglot translates the DOM, so a string built here
  // would ship untranslated); this list only decides which of those blocks is
  // unhidden, matching on `data-lr-config-notice`.
  //
  // First match wins, among the notices this product actually rendered — a rule
  // whose block is absent from the page is skipped. Only the mid-November notice
  // (which carries the waitlist) is left; every other bike shows no notice at all.

  const ETA_NOTICES = [
    { id: 'mid-november', match: isMidNovember }
  ];

  // Order matters — each step only offers values still reachable given the
  // steps above it.
  const STEPS = [
    { key: 'make', label: 'Make', placeholder: 'Select Make' },
    { key: 'model', label: 'Model', placeholder: 'Select Model' },
    { key: 'lights', label: 'Lights', placeholder: 'Select Lights' },
    { key: 'motocan', label: 'MotoCAN', placeholder: '', locked: true },
    // Bar Clamp always ships with the lights, so Mount is not a pick-one dropdown:
    // it is an opt-in for each mount upgrade this bike offers (Under Nose, BMW OEM
    // Crash Bar Aux Light, or both), the first of them ticked by default. A ticked
    // box resolves to that upgrade's variant, none ticked to the Bar Clamp one.
    { key: 'mount', label: 'Mount', type: 'checkbox' }
  ];

  const SELECTORS = {
    section: '[data-make-configurator]',
    root: '[data-lr-config]',
    nativeSelect: 'select[data-variant-input][data-index="option1"]',
    variantJson: 'textarea[data-variant-json]',
    hiddenValues: '[data-hidden-variant-values]',
    notice: '[data-lr-config-notice]',
    noticeModel: '[data-lr-config-notice-model]',

    // The sticky cart bar hosts a second configurator so its panel offers the same
    // five steps as the PDP. That instance has no theme.Variants listening to it —
    // the bar posts the id from its own hidden input and paints its own price — so
    // these are the handles it writes instead. See updateSticky().
    stickyBar: '[data-sticky-cart-bar]',
    stickyPanel: '.sticky-cart-bar__above',
    stickyVariantId: '[data-sticky-variant-id]',
    stickyPrice: '[data-sticky-price]',
    stickyCompare: '[data-sticky-compare-price]',
    stickyCompareWrap: '[data-sticky-compare-wrap]'
  };

  const escapeHtml = (str) => String(str)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  // Only the sticky bar needs this: on the PDP theme.Variants renders the price.
  const formatMoney = (cents) => {
    if (window.theme && theme.Currency && theme.settings && theme.settings.moneyFormat) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  };

  // ---------- Parsing ----------

  // Splits one option1 string into its five configurator fields.
  //
  // The pipe is NOT a fixed boundary — the catalogue uses it differently for the two
  // shapes we have to support:
  //
  //   bike        "BMW R1300GS Adventure | MTL9 ... + MotoCAN V2 + Under Nose Mount"
  //   other brand "MTL9 ... (2 Pods) & MTL4 ... (2 Pods) + Bar Clamp Mount | No MotoCAN V2"
  //
  // So instead of splitting on position, every segment is classified by keyword. The
  // bike segment is simply the one with no lights / mount / MotoCAN keyword in it;
  // Other Brand variants just don't have one.
  //
  // Returns null for values that aren't configurator-shaped, which the caller drops.
  function parseOptionText(text) {
    const raw = String(text == null ? '' : text).trim();
    if (!raw || raw.indexOf('DO NOT USE') !== -1) return null;

    const segments = raw.split('|').map((s) => s.trim()).filter(Boolean);
    if (!segments.length) return null;

    // The bike segment is never split on "+", so a model written like
    // "CRF1100L Africa Twin (31+38L)" survives intact.
    let bikeSegment = '';
    const partSegments = [];
    segments.forEach((seg) => {
      if (!bikeSegment && !hasPartKeyword(seg)) bikeSegment = seg;
      else partSegments.push(seg);
    });

    const parts = [];
    partSegments.forEach((seg) => {
      seg.split(/[+&]/).map((s) => s.trim()).filter(Boolean).forEach((p) => parts.push(p));
    });
    if (!parts.length) return null;

    // --- Make / Model ---
    let make = '';
    let model = '';
    if (bikeSegment) {
      model = bikeSegment;
      for (let i = 0; i < MAKES_BY_LENGTH.length; i++) {
        const candidate = MAKES_BY_LENGTH[i];
        const rx = MAKE_RX.get(candidate);
        if (rx.test(bikeSegment)) {
          make = candidate;
          model = bikeSegment.replace(rx, '').replace(/\s{2,}/g, ' ').trim();
          break;
        }
      }
    }
    // No bike at all, or a bike whose make isn't in MAKES: file under Other Brand
    // rather than dropping the variant, so it stays buyable. With no bike segment
    // the model is empty, which is what makes the Model step skip itself.
    if (!make) make = OTHER_BRAND;

    // Fold a known-misspelled model onto its canonical spelling BEFORE the key is
    // taken, so the variant joins the right bike's row rather than starting one of
    // its own. Other Brand kits have no model, and normKey('') matches no alias.
    const modelAlias = MODEL_ALIASES[normKey(model)];
    if (modelAlias) model = modelAlias;

    // --- Lights / MotoCAN / Mount ---
    const lights = [];
    const motocan = [];
    const mount = [];

    parts.forEach((part) => {
      if (MOTOCAN_RX.test(part)) motocan.push(part);
      else if (MOUNT_RX.test(part)) mount.push(part);
      else lights.push(part);
    });

    const lightsText = lights.join(' & ');
    const motocanText = motocan.join(' + ');
    const mountText = mount.join(' + ');

    return {
      raw: raw,
      // What the customer sees — the catalogue's own wording.
      make: make,
      model: model,
      lights: lightsText,
      motocan: motocanText,
      mount: mountText,
      // Every non-bike part, in order, for the What's-in-the-box list.
      parts: parts,
      // "No MotoCAN V2" — the step is answered but hidden, since there is
      // nothing for the customer to choose.
      noMotocan: motocanText !== '' && NO_WORD_RX.test(motocanText),
      // What we match on — spelling-independent.
      keys: {
        make: make,
        model: normKey(model),
        lights: lightsKey(lightsText),
        motocan: normKey(motocanText),
        mount: mountKey(mountText)
      }
    };
  }

  // ---------- Variant data ----------

  // Falls back to the page's own variant JSON when the scope carries none. That is
  // the sticky bar's case: it is a second view of the product the PDP already
  // printed every variant for, and this catalogue is far too big to print twice.
  // Quick shop modals do carry their own, and match on the scope first.
  function readVariants(scope) {
    const el = (scope && scope.querySelector(SELECTORS.variantJson)) ||
      document.querySelector(SELECTORS.variantJson);
    if (!el) return [];
    try {
      const parsed = JSON.parse(el.value || el.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error('[LR configurator] variant JSON parse failed', e);
      return [];
    }
  }

  // Option values flagged via the custom.hide_variant metafield.
  function readHiddenValues(scope) {
    const el = (scope && scope.querySelector(SELECTORS.hiddenValues)) ||
      document.querySelector(SELECTORS.hiddenValues);
    if (!el) return new Set();
    try {
      const arr = JSON.parse(el.textContent);
      return Array.isArray(arr) ? new Set(arr.map(String)) : new Set();
    } catch (e) {
      return new Set();
    }
  }

  function buildEntries(variants, hiddenValues) {
    const entries = [];
    const skipped = [];

    variants.forEach((variant) => {
      const value = String((variant.options && variant.options[0]) || variant.option1 || '');
      if (!value || hiddenValues.has(value)) return;

      const parsed = parseOptionText(value);
      if (!parsed) {
        skipped.push(value);
        return;
      }
      entries.push({ variant: variant, parsed: parsed, keys: parsed.keys, available: !!variant.available });
    });

    if (skipped.length) {
      console.warn(
        '[LR configurator] ' + skipped.length + ' variant(s) are not in "Make Model | A + B + C" ' +
        'format and were left out of the configurator:', skipped
      );
    }

    return entries;
  }

  // ---------- DOM building ----------

  // The mount-upgrade opt-in. Not a dropdown — Bar Clamp is included with the
  // lights, so these are upgrade ticks rather than a pick-one list. The rows
  // themselves are built per bike by renderCheckbox(), since how many upgrades
  // exist depends on the selection above.
  function buildCheckboxStep(step) {
    const wrapper = document.createElement('div');
    wrapper.className = 'variant-wrapper js lr-config__step lr-config__step--checkbox';
    wrapper.setAttribute('data-lr-config-step', step.key);

    const group = document.createElement('div');
    group.className = 'lr-config__checkbox-group';
    wrapper.appendChild(group);

    return { wrapper: wrapper, group: group, isCheckbox: true, rows: [], signature: null };
  }

  function buildStep(step) {
    if (step.type === 'checkbox') return buildCheckboxStep(step);

    const wrapper = document.createElement('div');
    wrapper.className = 'variant-wrapper variant-wrapper--dropdown js lr-config__step';
    wrapper.setAttribute('data-lr-config-step', step.key);

    const label = document.createElement('label');
    label.className = 'variant__label';
    label.textContent = step.label;

    const inputWrap = document.createElement('div');
    inputWrap.className = 'variant-input-wrap';

    const cd = document.createElement('div');
    cd.className = 'lr-select' + (step.locked ? ' lr-select--locked' : '');

    const trigger = document.createElement('div');
    trigger.className = 'lr-select__trigger';

    const labelEl = document.createElement('span');
    labelEl.className = 'lr-select__label';

    const arrow = document.createElement('span');
    arrow.className = 'lr-select__arrow';
    arrow.innerHTML = '<svg viewBox="0 0 10 6" width="10" height="6" aria-hidden="true"><path d="M0 0l5 6 5-6z" fill="currentColor"/></svg>';

    trigger.appendChild(labelEl);
    if (!step.locked) trigger.appendChild(arrow);

    const list = document.createElement('ul');
    list.className = 'lr-select__list';

    cd.appendChild(trigger);
    cd.appendChild(list);
    inputWrap.appendChild(cd);
    wrapper.appendChild(label);
    wrapper.appendChild(inputWrap);

    return { wrapper: wrapper, cd: cd, trigger: trigger, labelEl: labelEl, list: list };
  }

  // ---------- Instance registry ----------
  //
  // A page carries more than one configurator for the same product: the PDP picker
  // and the one inside the sticky cart bar are two views of the same five answers,
  // so a pick in either has to move the other. Instances register here; picking
  // pushes that instance's state to its peers, and a peer adopting state re-renders
  // WITHOUT pushing back, so the two can never ping-pong.
  //
  // Quick shop and cart upsell modals are deliberately left out of the sync: a modal
  // is a separate purchase of the same product, not another view of the one being
  // configured on the page behind it.
  const INSTANCES = [];
  let syncing = false;

  function syncPeers(source) {
    if (syncing) return;
    syncing = true;
    try {
      for (let i = INSTANCES.length - 1; i >= 0; i--) {
        const inst = INSTANCES[i];
        // A modal is thrown away with its DOM — drop its instance with it.
        if (!document.contains(inst.root)) { INSTANCES.splice(i, 1); continue; }
        if (inst === source || inst.productId !== source.productId) continue;
        if (getModalHolder(inst.root) || getModalHolder(source.root)) continue;
        inst.applyState(source.state);
      }
    } finally {
      syncing = false;
    }
  }

  // ---------- Instance ----------

  function createConfigurator(section) {
    const root = section.querySelector(SELECTORS.root);
    const nativeSelect = section.querySelector(SELECTORS.nativeSelect);
    if (!root || !nativeSelect || root.__lrConfig) return null;

    // An instance rendered inside the sticky cart bar drives the bar rather than
    // theme.Variants. Everything above this line — the steps, the parsing, the ETA
    // notices — is identical either way; only resolve() branches.
    const stickyBar = section.closest(SELECTORS.stickyBar);
    const stickyPanel = stickyBar && stickyBar.querySelector(SELECTORS.stickyPanel);

    // What syncPeers() matches on, so the bar and the PDP only ever move each other.
    const pidEl = section.closest('[data-product-id]');
    const productId = String((pidEl && pidEl.getAttribute('data-product-id')) || '');

    const variants = readVariants(section);
    if (!variants.length) return null;

    const entries = buildEntries(variants, readHiddenValues(section));
    if (!entries.length) {
      console.warn('[LR configurator] no parseable variants — leaving the native picker in place');
      return null;
    }

    // theme.js VariantAvailability may strip <option> elements before we run, which
    // would make `nativeSelect.value = ...` silently fail. Rebuild the full list from
    // the variant JSON so every value we can select definitely exists.
    const known = new Set(Array.from(nativeSelect.options).map((o) => o.value));
    entries.forEach((entry) => {
      if (known.has(entry.parsed.raw)) return;
      known.add(entry.parsed.raw);
      const opt = document.createElement('option');
      opt.value = entry.parsed.raw;
      opt.textContent = entry.parsed.raw;
      nativeSelect.appendChild(opt);
    });

    // Bar Clamp ships with every kit, so it belongs in the box list even when the
    // customer opted for a mount upgrade. Take the catalogue's own wording where we have
    // it so the list reads consistently with the variant titles.
    const barClampEntry = entries.find((e) => e.keys.mount === 'barclamp');
    const barClampLabel = (barClampEntry && barClampEntry.parsed.mount) || 'Bar Clamp Mount';

    const state = { make: '', model: '', lights: '', motocan: '', mount: '' };
    // Which steps are in play for the current selection. A step with no reachable
    // values (Model under Other Brand) is skipped rather than blocking the chain.
    const applicable = { make: true, model: true, lights: true, motocan: true, mount: true };
    const ui = {};

    STEPS.forEach((step) => {
      const built = buildStep(step);
      ui[step.key] = built;
      root.appendChild(built.wrapper);

      // Checkbox rows are created and wired up per bike, in renderCheckbox().
      if (built.isCheckbox) return;

      if (step.locked) return;

      built.trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const wasOpen = built.cd.classList.contains('is-open');
        closeAll(root);
        if (!wasOpen) {
          built.cd.classList.add('is-open');
          revealInPanel(built);
        }
      });

      built.list.addEventListener('click', (e) => {
        const item = e.target.closest('.lr-select__item');
        if (!item || item.classList.contains('is-hidden')) return;
        // Stop the document handler below from closing the step we are about to open.
        e.stopPropagation();
        built.cd.classList.remove('is-open');
        const value = item.getAttribute('data-value');
        if (!value) return; // placeholder row
        select(step.key, value);
      });
    });

    // Hide the native select but keep it in the DOM and in the form — theme.Variants
    // reads it to resolve the variant.
    nativeSelect.style.cssText = 'position:absolute;opacity:0;pointer-events:none;width:1px;height:1px;overflow:hidden;';
    const nativeWrap = nativeSelect.closest('.variant-wrapper');
    if (nativeWrap && nativeWrap !== root) nativeWrap.style.display = 'none';

    // A user picking step N invalidates every step below it.
    function select(key, value) {
      const idx = STEPS.findIndex((s) => s.key === key);
      state[key] = value;
      for (let i = idx + 1; i < STEPS.length; i++) state[STEPS[i].key] = '';
      refresh();
      syncPeers(instance);
      openNextUnanswered(idx);
    }

    // Adopt a peer's answers — the other half of syncPeers(). refresh() re-derives
    // everything else (which steps apply, what each list holds, the resolved
    // variant) from them, so the two views land on the same configuration.
    function applyState(next) {
      let changed = false;
      STEPS.forEach((step) => {
        const value = next[step.key] || '';
        if (state[step.key] === value) return;
        state[step.key] = value;
        changed = true;
      });
      if (changed) refresh();
    }

    // Inside the bar the steps live in a panel that scrolls on narrow screens, so a
    // step that has just appeared — or whose list is about to drop open — can sit
    // below the fold. Bring it to the top of the panel, which also gives its list
    // the panel's full height to open into. A no-op where the panel does not scroll.
    function revealInPanel(built) {
      if (!stickyPanel || stickyPanel.scrollHeight <= stickyPanel.clientHeight) return;
      const delta = built.wrapper.getBoundingClientRect().top - stickyPanel.getBoundingClientRect().top;
      stickyPanel.scrollTop += delta - 8;
    }

    // Cascade: answering a step immediately opens the next one that still needs an
    // answer, so the customer is walked down the list without hunting for the next
    // dropdown. Steps refresh() already filled in — MotoCAN (locked to its single
    // value) and Mount (defaulted to the upgrade) — are skipped, so the chain ends
    // on the last real choice rather than opening a dropdown that is already correct.
    function openNextUnanswered(fromIdx) {
      for (let i = fromIdx + 1; i < STEPS.length; i++) {
        const step = STEPS[i];
        // Skipped steps (Model under Other Brand) are stepped over, not stopped on.
        if (!applicable[step.key]) continue;
        if (step.locked || state[step.key]) continue;

        const built = ui[step.key];
        if (built.wrapper.classList.contains('is-hidden')) break;
        // Nothing to choose from — don't open an empty list.
        if (!built.list.querySelector('.lr-select__item[data-value]:not([data-value=""])')) break;

        closeAll(root);
        built.cd.classList.add('is-open');
        revealInPanel(built);
        return;
      }
      closeAll(root);
    }

    // Choices still reachable for this step given the selections above it.
    // Returns { key, label } — deduped by key, so two spellings of the same choice
    // collapse into one row, labelled with whichever spelling appeared first.
    function allowedFor(stepIndex) {
      const stepKey = STEPS[stepIndex].key;
      const choices = [];
      const seen = new Set();

      entries.forEach((entry) => {
        if (!entry.available) return;
        for (let i = 0; i < stepIndex; i++) {
          const prior = STEPS[i].key;
          if (state[prior] && entry.keys[prior] !== state[prior]) return;
        }
        const key = entry.keys[stepKey];
        // An empty key means the step doesn't apply to this variant (Other Brand
        // kits have no model). It contributes no row, which is what makes the step
        // skip itself entirely.
        if (!key || seen.has(key)) return;
        seen.add(key);
        // first-seen order, matching the admin's variant order
        choices.push({ key: key, label: entry.parsed[stepKey] });
      });

      // Real bikes first, the no-bike kit last.
      if (stepKey === 'make') {
        choices.sort((a, b) => (a.key === OTHER_BRAND ? 1 : 0) - (b.key === OTHER_BRAND ? 1 : 0));
      }

      return choices;
    }

    // Whether this step applies at all, given the steps above it.
    //
    // Answered from whether the FIELD exists on reachable variants, deliberately
    // ignoring stock. Deriving this from allowedFor() instead would mean a step with
    // nothing in stock counted as "not applicable" and hid itself — and if that hit
    // the Make step, every step below it hid too and the whole configurator vanished.
    // Out of stock must mean "empty list", never "no step".
    function stepApplies(stepIndex) {
      if (stepIndex === 0) return true; // Make is always shown
      const stepKey = STEPS[stepIndex].key;
      return entries.some((entry) => {
        for (let i = 0; i < stepIndex; i++) {
          const prior = STEPS[i].key;
          if (state[prior] && entry.keys[prior] !== state[prior]) return false;
        }
        return !!entry.keys[stepKey];
      });
    }

    // Mount-upgrade opt-in — one tick per upgrade. `allowed` holds whichever mounts
    // are actually in stock for the current bike + lights, so a bike offering both
    // Under Nose and the BMW OEM Crash Bar mount gets a row for each.
    //
    // The variant carries exactly ONE mount, so the ticks are mutually exclusive:
    // ticking one unticks the others, and unticking the ticked one falls back to the
    // included Bar Clamp. They are still checkboxes rather than radios because
    // "no upgrade" is a real answer the customer has to be able to get back to.
    function renderCheckbox(step, built, allowed) {
      const upgrades = upgradeMounts(allowed);
      const off = allowed.find((c) => c.key === MOUNT_INCLUDED_KEY);

      built.offKey = off ? off.key : '';

      // Rebuild the rows only when the offered upgrades actually change. refresh()
      // runs on every selection, and replacing the inputs each time would tear out
      // the very checkbox whose `change` event is being handled.
      const signature = JSON.stringify(upgrades.map((c) => [c.key, c.label]));
      if (built.signature !== signature) {
        built.signature = signature;
        built.group.innerHTML = '';
        built.rows = upgrades.map((choice) => {
          const label = document.createElement('label');
          label.className = 'lr-config__checkbox';

          const input = document.createElement('input');
          input.type = 'checkbox';
          input.className = 'lr-config__checkbox-input';

          const text = document.createElement('span');
          text.className = 'lr-config__checkbox-text';
          text.textContent = choice.label;

          label.appendChild(input);
          label.appendChild(text);
          built.group.appendChild(label);

          input.addEventListener('change', () => {
            if (input.checked) {
              select(step.key, choice.key);
              return;
            }
            // Unticking means "just the included Bar Clamp". With no Bar Clamp
            // variant for this bike there is nothing to fall back to, so the tick
            // stays put rather than leaving the step unanswered.
            if (!built.offKey) {
              input.checked = true;
              return;
            }
            select(step.key, built.offKey);
          });

          return { key: choice.key, input: input };
        });
      }

      built.rows.forEach((row) => {
        row.input.checked = state[step.key] === row.key;
      });

      // Nothing to opt into unless there is more than one mount for this combination.
      built.nothingToTick = allowed.length < 2;
      built.wrapper.classList.toggle('lr-config__step--single', built.nothingToTick);
    }

    function renderList(step, built, allowed) {
      built.list.innerHTML = '';
      let selectedLabel = '';

      if (step.placeholder && !state[step.key]) {
        const ph = document.createElement('li');
        ph.className = 'lr-select__item lr-select__item--placeholder';
        ph.setAttribute('data-value', '');
        ph.textContent = step.placeholder;
        built.list.appendChild(ph);
      }

      allowed.forEach((choice) => {
        const li = document.createElement('li');
        li.className = 'lr-select__item';
        li.setAttribute('data-value', choice.key);
        li.setAttribute('data-label', choice.label);
        li.textContent = choice.label;
        if (choice.key === state[step.key]) {
          li.classList.add('is-selected');
          selectedLabel = choice.label;
        }
        built.list.appendChild(li);
      });

      built.labelEl.textContent = selectedLabel || step.placeholder;
      built.cd.classList.toggle('is-empty', !state[step.key]);
    }

    function refresh() {
      // The page loads showing Make only; each step appears as the one above it is
      // answered. `revealed` walks down the chain and goes false at the first
      // unanswered step, hiding everything below it.
      let revealed = true;

      STEPS.forEach((step, i) => {
        const built = ui[step.key];
        const allowed = allowedFor(i);

        // A step whose field is absent from the reachable variants does not apply —
        // Other Brand kits have no model, so Make hands straight over to Lights.
        // It is hidden and, crucially, does NOT stop the chain. Note this is about
        // the field being absent, NOT about stock: see stepApplies().
        const applies = stepApplies(i);
        applicable[step.key] = applies;

        // Drop a selection that the steps above it have just made unreachable.
        if (state[step.key] && !allowed.some((c) => c.key === state[step.key])) state[step.key] = '';

        if (!applies || !revealed) {
          state[step.key] = '';
        } else {
          // MotoCAN is locked to its single value; Mount defaults to the upgrade ticked.
          // Make / Model / Lights are never auto-filled — the customer picks.
          // This only runs once the step is actually in play, otherwise MotoCAN and
          // Mount would answer themselves on load and drag their rows into view.
          if (!state[step.key]) {
            if (step.locked) {
              state[step.key] = allowed[0].key;
            } else if (built.isCheckbox) {
              const on = upgradeMount(allowed);
              state[step.key] = on ? on.key : (allowed[0] ? allowed[0].key : '');
            }
          }
        }

        if (built.isCheckbox) {
          renderCheckbox(step, built, allowed);
        } else {
          renderList(step, built, allowed);
        }

        // Hide the MotoCAN row when the only thing on offer is "No MotoCAN V2" —
        // there is nothing to choose, so showing it just adds noise.
        // The !! matters: `step.locked` is undefined on the other steps, which would
        // make this undefined, and classList.toggle(cls, undefined) TOGGLES rather
        // than sets — flipping each step's visibility on every refresh.
        const onlyNegated = !!(step.locked && allowed.length === 1 && NO_WORD_RX.test(allowed[0].label));
        // Same idea for the mount ticks: if only one mount is in stock for this
        // combination there is nothing to opt into, so don't show a dead checkbox.
        const nothingToTick = !!(built.isCheckbox && built.nothingToTick);
        built.wrapper.classList.toggle('is-hidden', !revealed || !applies || onlyNegated || nothingToTick);

        // A step that doesn't apply is transparent to the chain; one that does must
        // be answered before the next appears.
        if (applies) revealed = revealed && !!state[step.key];
      });

      resolve();
    }

    function findEntry() {
      return entries.find((entry) => {
        if (!entry.available) return false;
        return STEPS.every((step) => {
          // A skipped step must match variants that also lack that field, so an
          // Other Brand kit is never matched by a variant that does have a model.
          if (!applicable[step.key]) return !entry.keys[step.key];
          return state[step.key] && entry.keys[step.key] === state[step.key];
        });
      }) || null;
    }

    // The option value we last handed to theme.Variants, and the only reliable
    // record of what the theme currently believes is selected.
    //
    // The select's own value must NOT be used for that: theme.VariantAvailability
    // rebuilds the option list on load by removing every <option> and re-inserting
    // the in-stock ones. Removing options walks the selection down the list one at a
    // time, and a removed <option> keeps its selectedness, so they all come back
    // flagged as selected and the browser keeps the LAST one — leaving the select
    // sitting on the final variant in the catalogue with no `change` event and the
    // theme none the wiser. Comparing against that value made the configurator skip
    // its own dispatch for exactly that one combination (on MTL9: Other Brand + the
    // pods kit), so the price, the gallery and the id posted to the cart all stayed
    // on the variant the page had loaded with.
    let pushedValue = null;

    function resolve() {
      const entry = findEntry();
      // Bikes in the mid-November batch cannot be ordered yet, so a resolved variant
      // is not automatically a buyable one.
      const orderable = !!entry && !isMidNovember(entry.parsed);

      // Narrower than `!orderable`: true only once a mid-November bike is actually
      // resolved, never while the configurator is merely unfinished. It is what takes
      // the price, quantity and add-to-cart off the page, and an incomplete
      // configurator must keep showing the price it loaded with.
      const waitlisted = !!entry && !orderable;

      if (!entry) {
        pushedValue = null;
      } else if (pushedValue !== entry.parsed.raw) {
        pushedValue = entry.parsed.raw;
        nativeSelect.value = entry.parsed.raw;
        // Only the PDP's select has theme.Variants listening on it. The bar's select
        // stays in step so the form is honest about what it holds, but the bar itself
        // is painted by updateSticky() below.
        if (!stickyBar) nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (stickyBar) {
        updateSticky(entry, orderable);
        setWaitlisted(stickyBar, waitlisted);
      } else {
        // After the dispatch above, or theme.Variants would re-enable the button off
        // the `change` event.
        setAtcEnabled(section, orderable);
        setWaitlisted(getModalHolder(section) || section, waitlisted);
      }

      updateWitb(entry);
      updateEtaNotice(entry);
    }

    // The sticky equivalent of handing the variant to theme.Variants: the bar carries
    // its own hidden `id` input and its own price, and this writes both.
    //
    // It deliberately does NOT disable the add-to-cart button. While the panel is shut
    // that button's job is to open it, so whether it can add to the cart is published
    // as a flag and sticky-cart-bar.liquid decides, from this plus the panel's state,
    // off the `lr:configchange` event.
    function updateSticky(entry, orderable) {
      const variant = entry && entry.variant;

      if (variant) {
        const idEl = stickyBar.querySelector(SELECTORS.stickyVariantId);
        if (idEl && String(idEl.value) !== String(variant.id)) idEl.value = String(variant.id);

        stickyBar.querySelectorAll(SELECTORS.stickyPrice).forEach((el) => {
          el.innerHTML = formatMoney(variant.price);
        });

        const onSale = !!(variant.compare_at_price && variant.compare_at_price > variant.price);
        const compareEl = stickyBar.querySelector(SELECTORS.stickyCompare);
        const compareWrap = stickyBar.querySelector(SELECTORS.stickyCompareWrap);
        if (compareEl) compareEl.innerHTML = onSale ? formatMoney(variant.compare_at_price) : '';
        if (compareWrap) compareWrap.style.display = onSale ? '' : 'none';
      }

      stickyBar.setAttribute('data-lr-config-ready', orderable ? 'true' : 'false');
      stickyBar.dispatchEvent(new CustomEvent('lr:configchange'));
    }

    // Shows the one notice that applies to the resolved variant, or none while the
    // configurator is still incomplete. Scoped to this section so a quick shop modal
    // never toggles the notice on the PDP behind it.
    function updateEtaNotice(entry) {
      const notices = section.querySelectorAll(SELECTORS.notice);
      if (!notices.length) return;

      // Only the notices rendered for this product can win — see ETA_NOTICES.
      const rendered = [];
      notices.forEach((el) => rendered.push(el.getAttribute('data-lr-config-notice')));

      const rule = entry
        ? ETA_NOTICES.find((r) => rendered.indexOf(r.id) > -1 && r.match(entry.parsed))
        : null;
      notices.forEach((el) => {
        el.hidden = !rule || el.getAttribute('data-lr-config-notice') !== rule.id;
      });

      // Drop the bike's own name into the sentence. Only the name is written from
      // here — the wording around it is rendered by make-configurator.liquid so
      // Weglot can translate it, and a bike name is a proper noun that stays put in
      // every language. Other Brand kits have no model, so nothing is inserted.
      if (!rule) return;
      const bike = [entry.parsed.make, entry.parsed.model]
        .filter((part) => part && part !== OTHER_BRAND)
        .join(' ')
        .trim();

      section.querySelectorAll(SELECTORS.noticeModel).forEach((el) => {
        if (el.textContent !== bike) el.textContent = bike;
      });
    }

    // Mirrors updateWitb() in lr-make-filter.js: everything right of the pipe is an
    // included item, unless it reads as an exclusion ("No ...").
    function updateWitb(entry) {
      // WITB is a main-PDP concern only — not a quick shop modal's, and not the
      // sticky bar's, which shares the PDP's list rather than owning one.
      if (stickyBar || getModalHolder(section)) return;

      const incl = document.getElementById('witb_incl_list');
      const excl = document.getElementById('witb_excl_list');
      if (!incl || !excl) return;

      if (!entry) {
        incl.innerHTML = '';
        excl.innerHTML = '';
        return;
      }

      // Built from the parsed parts rather than by re-slicing the raw string, since
      // the pipe means different things in the bike and Other Brand formats.
      const included = [];
      const excluded = [];

      entry.parsed.parts.forEach((part) => (NO_WORD_RX.test(part) ? excluded : included).push(part));

      // Bar Clamp comes with the lights on every configuration, so list it even when
      // a mount upgrade was chosen. Guarded so the Bar Clamp variants — whose
      // parts already contain it — don't list it twice.
      if (!included.some((part) => mountKey(part) === 'barclamp')) included.push(barClampLabel);

      incl.innerHTML = included.map((i) => '<li class="product__items">' + escapeHtml(i) + '</li>').join('');
      excl.innerHTML = excluded.map((i) => '<li class="product__items">' + escapeHtml(i) + '</li>').join('');
    }

    // The configurator starts blank on purpose, but a URL that names a variant is an
    // explicit request for that one — a shared link, the back button, or just a
    // refresh after configuring — and working through the five steps again by hand is
    // not a reasonable thing to ask. Seed the answers from it so the page comes back
    // where it was left.
    //
    // Runs before the first refresh(), which then derives everything else (which steps
    // apply, what each list holds, the resolved variant) from these answers exactly as
    // it would from ones the customer clicked.
    function seedFromUrl() {
      let id = '';
      try {
        id = new URLSearchParams(window.location.search).get('variant') || '';
      } catch (err) {
        return;
      }
      if (!id) return;

      // Matched against this instance's own entries, so the id has to name a variant
      // this configurator actually offers — an unrelated ?variant= is ignored.
      const entry = entries.find((candidate) => String(candidate.variant.id) === id);
      if (!entry) return;

      // All five at once. refresh() drops any answer the steps above it make
      // unreachable, so a link to a variant that has since sold out degrades to as much
      // of the configuration as still holds rather than to nothing.
      STEPS.forEach((step) => {
        state[step.key] = entry.keys[step.key] || '';
      });
    }

    seedFromUrl();

    const instance = {
      refresh: refresh,
      state: state,
      entries: entries,
      root: root,
      productId: productId,
      applyState: applyState
    };

    INSTANCES.push(instance);
    root.__lrConfig = instance;
    root.classList.add('lr-config--ready');

    refresh();

    return instance;
  }

  // ---------- Shared helpers ----------

  function getModalHolder(el) {
    if (!el) return null;
    return el.closest('[id^="QuickShopHolder-"]') || el.closest('#CartUpsellHolder') || null;
  }

  function setAtcEnabled(section, enabled) {
    const scope = getModalHolder(section) || section;
    if (!scope) return;
    scope.querySelectorAll('button[data-add-to-cart]').forEach((btn) => {
      btn.disabled = !enabled;
      btn.classList.toggle('disabled', !enabled);
    });
  }

  // Bikes in the mid-November batch cannot be bought at all — the waitlist form in
  // their notice is the only thing there is to do with them — so the price, the
  // quantity selector and the add-to-cart button come off the page rather than sit
  // there quoting a price for something nobody can order. Published as a class on
  // the same scope setAtcEnabled() uses, with the rules in custom.css, so nothing
  // here has to know the theme's block markup.
  function setWaitlisted(scope, waitlisted) {
    if (!scope) return;
    scope.classList.toggle('lr-config-waitlisted', !!waitlisted);
  }

  function closeAll(scope) {
    const target = scope || document;
    target.querySelectorAll('.lr-select.is-open').forEach((cd) => cd.classList.remove('is-open'));
  }

  // ---------- Klaviyo waitlist ----------
  //
  // The bikes in the mid-November batch cannot be bought, so the notice carries an
  // email capture. Submitting subscribes the address to one Klaviyo list and records
  // WHICH bike was being looked at, so the list is useful rather than anonymous.
  //
  // The handler is bound on `document` and this file loads on every page, so it also
  // serves the waitlist on products that never touch the configurator — the tagged
  // ones on the normal pickers (snippets/product-waitlist.liquid). Everything it
  // needs comes off the form and the notice around it, so there is nothing product-
  // specific here: `data-source` says how to label the signup, and the notice's
  // [data-lr-config-notice-model] says which bike, whichever script filled it in.
  //
  // Uses Klaviyo's client-side endpoint, which is CORS-enabled for browsers and
  // authenticates with the PUBLIC site id — the same key klaviyo.js already exposes
  // on every page. No private key is ever in the theme.
  //
  // The list uses single opt-in, so a 202 here means the address is subscribed
  // outright — no confirmation email, nothing pending. Double opt-in is a setting on
  // the LIST in Klaviyo, not something this request controls: if it is ever switched
  // back on there, the success wording in make-configurator.liquid has to change with
  // it, because a 202 would then only mean "confirmation email sent".
  const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  // Klaviyo requires a dated revision on every request and keeps old ones working,
  // so this is pinned rather than tracking latest.
  const KLAVIYO_REVISION = '2024-10-15';

  function submitWaitlist(form) {
    const companyId = form.getAttribute('data-company-id');
    const listId = form.getAttribute('data-list-id');
    const input = form.querySelector('input[type="email"]');
    const button = form.querySelector('[data-lr-waitlist-submit]');
    const ok = form.querySelector('[data-lr-waitlist-ok]');
    const err = form.querySelector('[data-lr-waitlist-err]');
    const email = input && input.value.trim();
    // How these signups are labelled in Klaviyo. One list serves every waitlist, so
    // the source is what tells them apart; the configurator's own form predates the
    // attribute, hence the default.
    const source = form.getAttribute('data-source') || 'MTL configurator waitlist';

    if (!companyId || !listId || !email) return;

    // The bike is read out of the notice this form sits in, which the configurator
    // has already filled in — one source of truth for what the customer picked.
    const notice = form.closest('[data-lr-config-notice]');
    const modelEl = notice && notice.querySelector(SELECTORS.noticeModel);
    const bike = (modelEl && modelEl.textContent.trim()) || '';

    if (ok) ok.hidden = true;
    if (err) err.hidden = true;
    if (button) button.disabled = true;

    const properties = {};
    if (bike) properties['Requested Bike'] = bike;

    // The exact variant, when the unavailable combination resolves to one. Set by
    // the MTL4+MTL9 combo configurator; absent on the PDP waitlists, which sign up
    // against a bike rather than a specific kit. Lets a coming-soon signup be matched
    // back to the variant that becomes available, not just to the bike's name.
    const variantId = form.getAttribute('data-variant-id');
    if (variantId) properties['Requested Variant ID'] = variantId;

    fetch(KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: { 'content-type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            // Shows in Klaviyo as the profile's source, so these signups are
            // distinguishable from the footer newsletter ones.
            custom_source: bike ? source + ' - ' + bike : source,
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email, properties: properties }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: listId } } }
        }
      })
    })
      .then((res) => {
        if (!res.ok) throw new Error('Klaviyo responded ' + res.status);
        if (ok) ok.hidden = false;
        if (input) input.value = '';
      })
      .catch((e) => {
        console.error('[LR configurator] waitlist signup failed', e);
        if (err) err.hidden = false;
      })
      .then(() => {
        if (button) button.disabled = false;
      });
  }

  if (!document.__lrWaitlistBound) {
    document.__lrWaitlistBound = true;
    document.addEventListener('submit', (e) => {
      const form = e.target && e.target.closest ? e.target.closest('[data-lr-waitlist]') : null;
      if (!form) return;
      e.preventDefault();
      submitWaitlist(form);
    });
  }

  // ---------- Init ----------

  function init(scope) {
    const host = scope || document;
    const sections = host.matches && host.matches(SELECTORS.section)
      ? [host]
      : Array.from(host.querySelectorAll(SELECTORS.section));

    sections.forEach((section) => {
      try {
        createConfigurator(section);
      } catch (e) {
        console.error('[LR configurator] init failed', e);
      }
    });
  }

  if (!document.__lrConfigClickBound) {
    document.__lrConfigClickBound = true;
    document.addEventListener('click', () => closeAll(document));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  // Quick shop modals inject their product HTML after load; lr-make-filter.js
  // calls this once the modal markup is in the DOM.
  window.LRMakeConfigurator = {
    init: init,
    parseOptionText: parseOptionText,
    MAKES: MAKES,
    // Read by lr-moto-lights-configurator.js (the combined MTL4+MTL9 landing page)
    // so that page does not keep a THIRD copy of these bikes. Exported rather than
    // copied: this stays the one place the batch is edited. Sliced so a consumer
    // cannot mutate the list this file matches on.
    MID_NOVEMBER_MODELS: MID_NOVEMBER_MODELS.slice()
  };
})();
